import {inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationStart, Router} from '@angular/router';
import {AuthService} from '@src/app/core/services/auth.service';
import {Walkthrough, WalkthroughItem} from '@src/app/shared/models/walkthrough.model';
import {ToastrService} from 'ngx-toastr';
import {TourService} from 'ngx-ui-tour-md-menu';
import {firstValueFrom} from 'rxjs';
import {ApiService} from '../api.service';

interface GuideSession {
    walkthroughId: string;
    context: string;
    completed: boolean;
    pending: Promise<void>;
}

@Injectable({providedIn: 'root'})
export class WalkthroughService {
    public readonly tourService = inject(TourService<WalkthroughItem>);
    private readonly auth = inject(AuthService);
    private readonly api = inject(ApiService);
    private readonly toast = inject(ToastrService);
    private readonly router = inject(Router);
    private registeredFeature: string | null = null;
    private session: GuideSession | null = null;
    private finishing = Promise.resolve();
    private navigationVersion = 0;
    public starting = false;

    public constructor() {
        this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
            if (event instanceof NavigationStart) {
                this.navigationVersion++;
                this.registeredFeature = null;
                void this.stop();
            }
        });
        this.tourService.events$.pipe(takeUntilDestroyed()).subscribe(event => {
            const session = this.session;
            if (!session) return;
            if (event.name === 'stepShow') {
                const step = (event.value as {step?: WalkthroughItem})?.step;
                if (step?.stepId && !step.completed) {
                    session.pending = session.pending.then(async () => {
                        if (session.context !== this.context()) return;
                        const response = await firstValueFrom(this.api.get(`walkthrough-items/${step.stepId}/complete`));
                        if (session.context !== this.context()) return;
                        this.auth.walkthroughs = response.data;
                        session.completed = !!response.data.find((guide: Walkthrough) => guide.id === session.walkthroughId)?.is_completed;
                    }).catch(() => this.errorMessage());
                }
            }
            if (event.name === 'end') {
                this.session = null;
                this.finishing = session.pending.then(async () => {
                    if (session.completed || session.context !== this.context()) return;
                    const response = await firstValueFrom(this.api.get(`walkthroughs/${session.walkthroughId}/drop-out`));
                    if (session.context === this.context()) this.auth.walkthroughs = response.data;
                }).catch(() => this.errorMessage());
            }
        });
    }

    public async initialize(): Promise<void> {
        const context = this.context();
        try {
            const response = await firstValueFrom(this.api.get('walkthroughs'));
            if (context === this.context()) this.auth.walkthroughs = response.data;
        } catch {
            // Keep cached guidance available if refreshing optional help fails.
        }
    }

    public get canReplayPage(): boolean { return !!this.pageGuide; }

    private get pageGuide(): Walkthrough | undefined {
        let route = this.router.routerState.snapshot.root;
        let feature: string | undefined;
        while (route) {
            feature = route.data?.['walkthrough'] ?? feature;
            route = route.firstChild;
        }
        // Settings tabs share one route; their mounted component supplies the specific guide.
        feature ??= this.router.url.startsWith('/app/settings/') ? this.registeredFeature : undefined;
        return this.auth.walkthroughs?.find(guide => guide.feature === feature);
    }

    public async replayPage(): Promise<void> {
        const guide = this.pageGuide;
        if (guide) await this.start(guide.feature, true);
    }

    // Existing page hooks register context only. Guides now open by explicit choice from Help.
    public async start(feature: string, restartTour: boolean): Promise<void> {
        if (!restartTour) {
            this.registeredFeature = feature;
            return;
        }
        if (this.starting) return;
        const guide = this.auth.walkthroughs?.find(item => item.feature === feature);
        if (!guide) return;
        const context = this.context();
        const url = this.router.url;
        const navigationVersion = this.navigationVersion;
        this.starting = true;
        try {
            await this.stop();
            // Reset by guide ID, including dismissed/completed guides whose items the API omits.
            const response = await firstValueFrom(this.api.get(`walkthroughs/${guide.id}/reset`));
            if (context !== this.context() || url !== this.router.url || navigationVersion !== this.navigationVersion) return;
            this.auth.walkthroughs = response.data;
            const refreshed = this.auth.walkthroughs.find(item => item.id === guide.id);
            const steps = this.availableSteps(refreshed?.items ?? []);
            if (!steps.length) {
                this.toast.info('There’s no guide for the controls currently shown on this page.');
                return;
            }
            this.tourService.initialize(steps, {enableBackdrop: false, closeOnOutsideClick: false, isOptional: true});
            this.session = {walkthroughId: guide.id, context, completed: false, pending: Promise.resolve()};
            this.tourService.start();
        } catch {
            this.session = null;
            this.errorMessage();
        } finally {
            this.starting = false;
        }
    }

    public async stop(): Promise<void> {
        if (this.session) this.tourService.end();
        await this.finishing;
    }

    public async reset(): Promise<boolean> {
        await this.stop();
        const context = this.context();
        try {
            const response = await firstValueFrom(this.api.get('walkthroughs/reset'));
            if (context !== this.context()) return false;
            this.auth.walkthroughs = response.data;
            return true;
        } catch {
            this.errorMessage();
            return false;
        }
    }

    private availableSteps(items: WalkthroughItem[]): WalkthroughItem[] {
        return items.flatMap(item => {
            try {
                const extra = typeof item.data === 'string' ? JSON.parse(item.data) : item.data ?? {};
                const anchor = this.tourService.anchors[item.anchorId]?.element.nativeElement as HTMLElement | undefined;
                if (!anchor?.getClientRects().length || getComputedStyle(anchor).visibility === 'hidden') return [];
                if (anchor.closest(':disabled, [aria-disabled="true"], [data-walkthrough-disabled="true"]')) return [];
                return [{...item, ...extra, stepId: item.stepId, anchorId: item.anchorId, route: undefined, nextStep: undefined, prevStep: undefined, isOptional: true, isAsync: false, enableBackdrop: false}];
            } catch {
                return [];
            }
        });
    }

    private context(): string { return `${this.auth.tenant?.id}:${this.auth.user?.id}`; }
    private errorMessage(): void { this.toast.error('Couldn’t load the page guide. Please try again.'); }
}
